/**
 * The zone registry, plus a validator.
 *
 * Two towns are built. There are no "coming soon" placeholders: a zone is in
 * this registry because it is finished, or it is not here at all.
 */
import { CINDERHOLD } from './cinderhold.js';
import { SALTMARCH } from './saltmarch.js';
export * from './types.js';
export { CINDERHOLD, SALTMARCH };
export const ZONES = [CINDERHOLD, SALTMARCH];
export const DEFAULT_ZONE_ID = CINDERHOLD.id;
export function getZone(id) {
    return ZONES.find((z) => z.id === id);
}
/** True when a point lies within a rotated rectangle, expanded by `margin`. */
function pointInFootprint(x, z, f, margin = 0) {
    const dx = x - f.centre[0];
    const dz = z - f.centre[1];
    const sin = Math.sin(-f.rotation);
    const cos = Math.cos(-f.rotation);
    const lx = f.rotation === 0 ? dx : dx * cos - dz * sin;
    const lz = f.rotation === 0 ? dz : dx * sin + dz * cos;
    return Math.abs(lx) <= f.half[0] + margin && Math.abs(lz) <= f.half[1] + margin;
}
function inBounds(p, zone) {
    return (p[0] >= zone.bounds.min[0] && p[0] <= zone.bounds.max[0] &&
        p[1] >= zone.bounds.min[1] && p[1] <= zone.bounds.max[1]);
}
/**
 * Structural checks on the zone data. Run in tests and once at boot in
 * development — a mistyped exit or a duplicated ad-slot id is the kind of thing
 * that otherwise shows up as a mysterious blank wall much later.
 */
export function validateZones(zones = ZONES) {
    const problems = [];
    const zoneIds = new Set();
    const allSlotIds = new Set();
    for (const zone of zones) {
        if (zoneIds.has(zone.id))
            problems.push(`duplicate zone id "${zone.id}"`);
        zoneIds.add(zone.id);
        const localIds = new Set();
        const claim = (kind, id) => {
            if (localIds.has(id))
                problems.push(`${zone.id}: duplicate ${kind} id "${id}"`);
            localIds.add(id);
        };
        zone.buildings.forEach((b) => claim('building', b.id));
        zone.props.forEach((p) => claim('prop', p.id));
        zone.roads.forEach((r) => claim('road', r.id));
        zone.water.forEach((w) => claim('water', w.id));
        // Ad slot ids are public and appear on invoices, so they must be unique
        // across the whole world, not just within one zone.
        for (const slot of zone.adSlots) {
            if (allSlotIds.has(slot.id))
                problems.push(`duplicate ad slot id "${slot.id}"`);
            allSlotIds.add(slot.id);
            if (!slot.showOnMobile && !slot.showOnDesktop) {
                problems.push(`${zone.id}: ad slot ${slot.id} is hidden on every device`);
            }
            if (slot.viewDistance <= 0) {
                problems.push(`${zone.id}: ad slot ${slot.id} has a non-positive view distance`);
            }
            if (slot.size[0] <= 0 || slot.size[1] <= 0) {
                problems.push(`${zone.id}: ad slot ${slot.id} has a non-positive size`);
            }
        }
        if (!inBounds([zone.spawn[0], zone.spawn[2]], zone)) {
            problems.push(`${zone.id}: spawn point is outside the zone bounds`);
        }
        /* Spawn placement. Both of these shipped broken in the first Stage 2 build:
           Saltmarch spawned the player inside the Charter Hall, and Cinderhold
           spawned half a metre from the Ember Shrine so the camera — which sits
           about 6 m behind — started inside its wall. Neither is visible in code
           review; both are obvious the moment you look at the screen. */
        const CAMERA_BACK = 7;
        const CLEARANCE = 1.2;
        for (const b of zone.buildings) {
            const half = [b.size[0] / 2, b.size[1] / 2];
            const f = { id: b.id, centre: b.position, half, rotation: b.rotation ?? 0 };
            if (pointInFootprint(zone.spawn[0], zone.spawn[2], f, CLEARANCE)) {
                problems.push(`${zone.id}: spawn point is inside or touching building "${b.id}"`);
            }
            // Where the camera starts, given the spawn facing.
            const camX = zone.spawn[0] - Math.sin(zone.spawnFacing) * CAMERA_BACK;
            const camZ = zone.spawn[2] - Math.cos(zone.spawnFacing) * CAMERA_BACK;
            if (pointInFootprint(camX, camZ, f, 0.5)) {
                problems.push(`${zone.id}: the camera would start inside building "${b.id}" — ` +
                    `move the spawn or turn it around`);
            }
        }
        for (const building of zone.buildings) {
            if (!inBounds(building.position, zone)) {
                problems.push(`${zone.id}: building "${building.id}" is outside the zone bounds`);
            }
            if (building.height <= 0) {
                problems.push(`${zone.id}: building "${building.id}" has a non-positive height`);
            }
        }
        for (const road of zone.roads) {
            if (road.points.length < 2) {
                problems.push(`${zone.id}: road "${road.id}" needs at least two points`);
            }
        }
        for (const exit of zone.exits) {
            if (!zones.some((z) => z.id === exit.toZone)) {
                problems.push(`${zone.id}: exit "${exit.id}" points at unknown zone "${exit.toZone}"`);
            }
            if (exit.toZone === zone.id) {
                problems.push(`${zone.id}: exit "${exit.id}" leads back to its own zone`);
            }
        }
    }
    return problems;
}
export function buildingFootprints(zone) {
    return zone.buildings.map((b) => ({
        id: b.id,
        centre: b.position,
        half: [b.size[0] / 2, b.size[1] / 2],
        rotation: b.rotation ?? 0
    }));
}
//# sourceMappingURL=index.js.map